import React, { useState } from 'react';
import { LinkIcon, PlusIcon, FileTextIcon, ScaleIcon, XIcon, GitPullRequestIcon, ListChecksIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { copy } from '../copy';
import { ConfidenceBand } from '../components/discovery/ConfidenceBand';
import { StatusPill } from '../components/discovery/StatusPill';
import { Provenance } from '../components/discovery/Provenance';
type Gap = (typeof copy.completeness.referentialGaps)[number];
export function CompletenessPage() {
  const c = copy.completeness;
  const [request, setRequest] = useState<Gap[]>([]);
  const [draft, setDraft] = useState('');
  const addGap = (gap: Gap) => {
    if (request.find(g => g.id === gap.id)) return;
    setRequest(prev => [...prev, gap]);
    setDraft(prev => prev + (prev ? '\n' : '') + `• ${gap.description} (Ref: ${gap.source})`);
  };
  const removeGap = (id: string) => setRequest(prev => prev.filter(g => g.id !== id));
  const renderGap = (gap: Gap) => <div key={gap.id} className="rounded-2xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-4 space-y-3" data-id="element-271">
      <div className="flex items-start justify-between gap-3" data-id="element-272">
        <p className="text-sm text-slate-700 dark:text-[#e5e5e5] leading-relaxed" data-id="element-273">
          {gap.description}
        </p>
        <StatusPill label={c.gapType[gap.type as keyof typeof c.gapType]} tone={gap.type === 'referential' ? 'purple' : 'blue'} data-id="element-274" />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-275">
        <Provenance source={gap.source} data-id="element-276" />
        <div className="flex items-center gap-3" data-id="element-277">
          <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-[#555]" data-id="element-278">
            Severity
            <ConfidenceBand value={gap.severity} compact data-id="element-279" />
          </span>
          <button onClick={() => addGap(gap)} disabled={!!request.find(g => g.id === gap.id)} className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-[#333] px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-[#a3a3a3] hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-40" data-id="element-280">
            <PlusIcon size={12} data-id="element-281" />
            {c.addToRequest}
          </button>
        </div>
      </div>
    </div>;
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-282">
      <Header title={c.headerTitle} minimal showBackArrow backTo="/case" data-id="element-283" />

      <main className="mx-auto max-w-6xl px-6 py-12" data-id="element-284">
        <div className="mb-8" data-id="element-285">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" data-id="element-286">
            {c.title}
          </h1>
          <p className="text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-287">
            {c.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-288">
          <div className="lg:col-span-2 space-y-8" data-id="element-289">
            <section className="space-y-3" data-id="element-290">
              <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white" data-id="element-291">
                <LinkIcon size={16} className="text-purple-500" data-id="element-292" />
                {c.referentialTitle}
              </h2>
              {c.referentialGaps.map(renderGap)}
            </section>
            <section className="space-y-3" data-id="element-293">
              <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white" data-id="element-294">
                <GitPullRequestIcon size={16} className="text-blue-500" data-id="element-295" />
                {c.sequenceTitle}
              </h2>
              {c.sequenceGaps.map(renderGap)}
            </section>
          </div>

          {/* Supplemental request composer */}
          <aside className="lg:col-span-1" data-id="element-296">
            <div className="sticky top-20 rounded-3xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4" data-id="element-297">
              <div data-id="element-298">
                <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white" data-id="element-299">
                  <ListChecksIcon size={16} className="text-emerald-500" data-id="element-300" />
                  {c.composerTitle}
                </h2>
                <p className="text-xs text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-301">
                  {c.composerSubtitle}
                </p>
              </div>

              <AnimatePresence data-id="element-302">
                {request.map(g => <motion.div key={g.id} initial={{
                opacity: 0,
                x: 8
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 8
              }} className="flex items-start gap-2 rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-2.5" data-id="element-303">
                    <span className="flex-1 text-xs text-slate-600 dark:text-[#a3a3a3] line-clamp-2" data-id="element-304">
                      {g.description}
                    </span>
                    <button onClick={() => removeGap(g.id)} className="text-slate-400 hover:text-red-500" data-id="element-305">
                      <XIcon size={14} data-id="element-306" />
                    </button>
                  </motion.div>)}
              </AnimatePresence>

              <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={6} placeholder="Selected gaps assemble here as an editable draft..." className="w-full rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] p-3 text-sm text-slate-700 dark:text-[#e5e5e5] outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-800 resize-none" data-id="element-307" />

              <div className="flex gap-2" data-id="element-308">
                <button disabled={request.length === 0} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 dark:bg-white px-3 py-2.5 text-sm font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 disabled:opacity-40 transition-all" data-id="element-309">
                  <FileTextIcon size={14} data-id="element-310" />
                  {c.exportLetter}
                </button>
                <button disabled={request.length === 0} className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-[#333] px-3 py-2.5 text-sm font-medium text-slate-600 dark:text-[#a3a3a3] hover:bg-white dark:hover:bg-[#1a1a1a] disabled:opacity-40 transition-all" data-id="element-311">
                  <ScaleIcon size={14} data-id="element-312" />
                  {c.exportMotion}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>;
}